import { useState, useMemo } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  AlertTriangle,
  Calendar,
  ChevronRight,
  TrendingUp,
  Users,
  X,
} from 'lucide-react'
import { useAuthStore } from '@/lib/auth-store'
import { ApiClientError } from '@/lib/api-client'
import { artistFetch } from '@/lib/artist-api'
import { cn } from '@/lib/utils'
import { TerritoryMap } from '@/components/TerritoryMap'
import { PerformanceCalendar } from '@/components/PerformanceCalendar'
import { parseLayersParam } from '@/pages/territory-map-helpers'

// ─── Types ───────────────────────────────────────────────────────────────────

interface QuarterBucket {
  label: string
  quarter: number
  year: number
  confirmedCount: number
}

interface PipelineBand {
  band: string
  label: string
  count: number
  totalValueMinor: number
  items: Array<{
    id: string
    stage: string
    contactName: string | null
    contactOrg: string | null
    eventCity: string | null
    eventDate: string | null
    budgetMax: number | null
  }>
}

interface OpenInquiry {
  id: string
  stage: string
  contactName: string | null
  contactOrg: string | null
  eventCity: string | null
  eventDate: string | null
  budgetMax: number | null
  daysSinceUpdate: number
}

interface BrokerAtRisk {
  brokerId: string
  brokerName: string
  territory: string
  sentimentScore: number | null
  sentimentLabel: string | null
  stalledDeals: number
}

// ─── Dashboard page ──────────────────────────────────────────────────────────

type DrillDown = 'quarters' | 'pipeline' | 'inquiries' | 'brokers' | null

export function DashboardPage() {
  const user = useAuthStore((s) => s.user)
  const [searchParams] = useSearchParams()
  const [drill, setDrill] = useState<DrillDown>(null)
  const mapLayers = useMemo(
    () => parseLayersParam(searchParams.get('layers')),
    [searchParams],
  )

  const quarters = useQuery<QuarterBucket[], ApiClientError>({
    queryKey: ['dashboard-quarters'],
    queryFn: () => artistFetch<QuarterBucket[]>('/shows/dashboard/quarters'),
    enabled: !!user?.artistId,
  })

  const bands = useQuery<PipelineBand[], ApiClientError>({
    queryKey: ['dashboard-pipeline-bands'],
    queryFn: () => artistFetch<PipelineBand[]>('/shows/dashboard/pipeline-bands'),
    enabled: !!user?.artistId,
  })

  const openInq = useQuery<OpenInquiry[], ApiClientError>({
    queryKey: ['dashboard-open-inquiries'],
    queryFn: () =>
      artistFetch<OpenInquiry[]>('/shows/dashboard/open-inquiries'),
    enabled: !!user?.artistId,
  })

  const brokersRisk = useQuery<BrokerAtRisk[], ApiClientError>({
    queryKey: ['dashboard-brokers-risk'],
    queryFn: () =>
      artistFetch<BrokerAtRisk[]>('/shows/dashboard/brokers-at-risk'),
    enabled: !!user?.artistId,
  })

  const totalPipelineValue = bands.data
    ? bands.data.reduce((s, b) => s + b.totalValueMinor, 0)
    : 0

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-display text-4xl tracking-tight">Dashboard</h1>
        <p className="mt-1 text-sm text-ovation-muted">
          Welcome back{user?.displayName ? `, ${user.displayName}` : ''}.
        </p>
      </header>

      {/* ── Hero cards ──────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {/* Quarterly shows */}
        {quarters.data?.map((q) => (
          <HeroCard
            key={q.label}
            icon={<Calendar className="h-4 w-4" />}
            label={q.label}
            value={q.confirmedCount}
            onClick={() => setDrill('quarters')}
          />
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {/* Pipeline value */}
        <HeroCard
          icon={<TrendingUp className="h-4 w-4" />}
          label="Pipeline value"
          value={`£${(totalPipelineValue / 100).toLocaleString()}`}
          subtitle={bands.data
            ?.map((b) => `${b.label}: ${b.count}`)
            .join(' · ')}
          onClick={() => setDrill('pipeline')}
        />

        {/* Open inquiries */}
        <HeroCard
          icon={<ChevronRight className="h-4 w-4" />}
          label="Open inquiries"
          value={openInq.data?.length ?? '—'}
          onClick={() => setDrill('inquiries')}
        />

        {/* Brokers at risk */}
        <HeroCard
          icon={<AlertTriangle className="h-4 w-4" />}
          label="Brokers at risk"
          value={brokersRisk.data?.length ?? 0}
          tone={
            brokersRisk.data && brokersRisk.data.length > 0
              ? 'warning'
              : undefined
          }
          onClick={() => setDrill('brokers')}
        />
      </div>

      <PerformanceCalendar />

      <TerritoryMap variant="dashboard" initialLayers={mapLayers} />

      {/* ── Drill-down panel ────────────────────────────────────────────── */}
      {drill && (
        <div className="rounded-lg border border-ovation-ink/10 bg-white p-6">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-display text-xl">
              {drill === 'quarters' && 'Confirmed shows by quarter'}
              {drill === 'pipeline' && 'Pipeline value breakdown'}
              {drill === 'inquiries' && 'Open inquiries'}
              {drill === 'brokers' && 'Brokers at risk'}
            </h2>
            <button
              type="button"
              onClick={() => setDrill(null)}
              className="text-ovation-muted hover:text-ovation-ink"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {drill === 'quarters' && <QuartersDrillDown data={quarters.data} />}
          {drill === 'pipeline' && <PipelineDrillDown data={bands.data} />}
          {drill === 'inquiries' && <InquiriesDrillDown data={openInq.data} />}
          {drill === 'brokers' && <BrokersDrillDown data={brokersRisk.data} />}
        </div>
      )}
    </div>
  )
}

// ─── Hero card ───────────────────────────────────────────────────────────────

function HeroCard({
  icon,
  label,
  value,
  subtitle,
  tone,
  onClick,
}: {
  icon: React.ReactNode
  label: string
  value: string | number
  subtitle?: string | undefined
  tone?: 'warning' | undefined
  onClick?: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-lg border p-5 text-left transition-all hover:shadow-md',
        tone === 'warning'
          ? 'border-amber-200 bg-amber-50 hover:border-amber-300'
          : 'border-ovation-ink/10 bg-white hover:border-ovation-ink/30',
      )}
    >
      <div className="flex items-center gap-1 text-xs uppercase tracking-wider text-ovation-muted">
        {icon}
        {label}
      </div>
      <div className="mt-2 font-display text-3xl">{value}</div>
      {subtitle && (
        <div className="mt-1 text-xs text-ovation-muted">{subtitle}</div>
      )}
    </button>
  )
}

// ─── Drill-down panels ───────────────────────────────────────────────────────

function QuartersDrillDown({ data }: { data: QuarterBucket[] | undefined }) {
  if (!data) return null
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {data.map((q) => (
        <div key={q.label} className="rounded-md border border-ovation-ink/10 p-4">
          <div className="font-display text-lg">{q.label}</div>
          <div className="mt-1 text-3xl font-bold">{q.confirmedCount}</div>
          <div className="mt-1 text-xs text-ovation-muted">confirmed shows</div>
          <Link
            to={`/shows?quarter=${q.quarter}&year=${q.year}`}
            className="mt-2 inline-flex text-xs text-ovation-ink hover:underline"
          >
            View shows →
          </Link>
        </div>
      ))}
    </div>
  )
}

function PipelineDrillDown({ data }: { data: PipelineBand[] | undefined }) {
  if (!data) return null
  return (
    <div className="space-y-4">
      {data.map((band) => (
        <div key={band.band}>
          <div className="mb-2 flex items-baseline justify-between">
            <h3 className="font-medium">{band.label}</h3>
            <span className="text-sm text-ovation-muted">
              {band.count} inquiries · £{(band.totalValueMinor / 100).toLocaleString()}
            </span>
          </div>
          {band.items.length > 0 ? (
            <div className="space-y-1">
              {band.items.slice(0, 10).map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between rounded-md border border-ovation-ink/5 bg-ovation-paper/30 px-3 py-2 text-sm"
                >
                  <div>
                    <span className="font-medium">
                      {item.contactName ?? item.contactOrg ?? '—'}
                    </span>
                    {item.eventCity && (
                      <span className="ml-2 text-ovation-muted">
                        {item.eventCity}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-ovation-muted">
                    {item.eventDate && (
                      <span>
                        {new Date(item.eventDate).toLocaleDateString()}
                      </span>
                    )}
                    {item.budgetMax && (
                      <span>
                        £{(item.budgetMax / 100).toLocaleString()}
                      </span>
                    )}
                    <span className="capitalize rounded bg-slate-100 px-1.5 py-0.5">
                      {item.stage.replace(/_/g, ' ')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-xs text-ovation-muted">None</div>
          )}
        </div>
      ))}
    </div>
  )
}

function InquiriesDrillDown({ data }: { data: OpenInquiry[] | undefined }) {
  if (!data) return null
  if (data.length === 0) {
    return (
      <div className="text-sm text-ovation-muted">
        No open inquiries. The pipeline is empty.
      </div>
    )
  }
  return (
    <div className="space-y-1">
      {data.map((inq) => (
        <div
          key={inq.id}
          className="flex items-center justify-between rounded-md border border-ovation-ink/5 bg-ovation-paper/30 px-3 py-2 text-sm"
        >
          <div>
            <span className="font-medium">
              {inq.contactName ?? inq.contactOrg ?? '—'}
            </span>
            {inq.eventCity && (
              <span className="ml-2 text-ovation-muted">{inq.eventCity}</span>
            )}
          </div>
          <div className="flex items-center gap-3 text-xs text-ovation-muted">
            <span className="capitalize rounded bg-slate-100 px-1.5 py-0.5">
              {inq.stage.replace(/_/g, ' ')}
            </span>
            <span
              className={cn(
                inq.daysSinceUpdate > 7
                  ? 'text-rose-600'
                  : inq.daysSinceUpdate > 3
                    ? 'text-amber-600'
                    : '',
              )}
            >
              {inq.daysSinceUpdate}d since update
            </span>
          </div>
        </div>
      ))}
    </div>
  )
}

function BrokersDrillDown({ data }: { data: BrokerAtRisk[] | undefined }) {
  if (!data || data.length === 0) {
    return (
      <div className="text-sm text-emerald-700">
        No brokers at risk. Sentiment is healthy across the board.
      </div>
    )
  }
  return (
    <div className="space-y-2">
      {data.map((b) => (
        <div
          key={b.brokerId}
          className="flex items-center justify-between rounded-md border border-amber-200 bg-amber-50 px-4 py-3"
        >
          <div>
            <div className="font-medium">{b.brokerName}</div>
            <div className="text-xs text-ovation-muted capitalize">
              {b.territory.replace(/_/g, ' ')}
            </div>
          </div>
          <div className="flex items-center gap-4 text-sm">
            {b.sentimentScore !== null && (
              <div
                className={cn(
                  'rounded px-2 py-0.5 text-xs',
                  b.sentimentScore < 30
                    ? 'bg-rose-100 text-rose-700'
                    : 'bg-amber-100 text-amber-700',
                )}
              >
                Score: {b.sentimentScore}/100
              </div>
            )}
            {b.stalledDeals > 0 && (
              <div className="text-xs text-rose-600">
                {b.stalledDeals} stalled deal{b.stalledDeals !== 1 ? 's' : ''}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}
